import { ChangeEvent, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Drawer, TextField, Typography } from '@arkestro/arkestro-design-system';
import useTranslation from 'next-translate/useTranslation';
import { Controller, useForm } from 'react-hook-form';

import { useUpdateLineItemColumnLabels } from '@features/event-level-column-rename/api/actions';
import { CANONICAL_COLUMN_ID, CLIENT_I18N_KEY, EVENT_SETTABLE_COLUMN_IDS } from '@features/event-level-column-rename/constants';
import { useColumnLabelResolver } from '@features/event-level-column-rename/hooks/use-column-label-resolver';
import { buyerOnlyPriceRowsFor } from '@features/event-level-column-rename/utils/buyer-only-price-rows';
import { columnAudienceHintKey } from '@features/event-level-column-rename/utils/column-audience';
import { extractLabelErrors } from '@features/event-level-column-rename/utils/extract-label-errors';
import { isColumnEnabledForEvent } from '@features/event-level-column-rename/utils/is-column-enabled-for-event';
import {
  labelValidationErrorMessage,
  labelsReservedByOtherColumns,
  validateColumnLabel,
} from '@features/event-level-column-rename/utils/validate-column-label';
import { LineItemsContext } from '@providers/LineItemsProvider/LineItemsContext';

import { DisabledColumnField } from './disabled-column-field';
import { RenameColumnsDrawerFooter } from './rename-columns-drawer-footer';
import styles from './rename-columns-drawer.module.scss';
import { BuyerOnlyColumnField } from './buyer-only-column-field';
import type {
  CanonicalColumnId,
  EventSettableColumnId,
  LineItemColumnLabels,
} from '@features/event-level-column-rename/types';

const DRAWER_WIDTH = 480;
const BUYER_ONLY_FIELD_TOOLTIP_KEY_PREFIX = 'bid_request.event_config_menu.column_labels.buyer_only_field_tooltips';

type ColumnLabelsFormValues = Record<EventSettableColumnId, string>;

const buildDefaultValues = (labels?: LineItemColumnLabels | null): ColumnLabelsFormValues =>
  EVENT_SETTABLE_COLUMN_IDS.reduce(
    (values, columnId) => ({ ...values, [columnId]: labels?.[columnId] ?? '' }),
    {} as ColumnLabelsFormValues
  );

interface RenameColumnsDrawerProps {
  onClose: () => void;
  open: boolean;
}

/**
 * The one place a buyer renames this event's columns: one input per renameable column, driven
 * by the label map itself rather than a second copy of the column list.
 * `negotiationPricePerUnit` has no input here: it is renameable organisation-wide only, and the
 * write endpoint rejects it.
 *
 * Two hosts open it, both mounted from `StickyHeader`: the Manage Column Display Names item in
 * the legacy event-configuration dropdown, and the row of the same name in the Line Item
 * Settings drawer. It stays host-agnostic — it reads the event off `LineItemsContext` rather
 * than taking it as props, so neither host plumbs the label payload through.
 *
 * The seed effect fires only on the closed→open transition. useForm applies `defaultValues` on
 * mount alone, so reopening must re-seed — but the save flow dispatches a refresh whose response
 * can land after the buyer has already reopened and started typing, handing `labels` a new
 * identity mid-edit. Seeding on the transition (never on a labels change while open) is what
 * keeps that late refresh from clobbering in-progress input.
 *
 * Which columns are editable is decided from the live `request` on every render, never from the
 * seed effect: the Line Item Settings drawer writes its toggles through the same provider, so a
 * setting flipped while this drawer is mounted is reflected immediately. A disabled column shows
 * its current effective name read-only and is dropped from the save payload at the boundary, so
 * its stored label is never touched (CONTRACT §8). `shouldUnregister` makes the form forget a
 * field the moment its setting is switched off, so a half-typed value or a validation error on a
 * now-disabled column cannot keep blocking Save for the columns that are still editable.
 */
export const RenameColumnsDrawer = ({ onClose, open }: RenameColumnsDrawerProps) => {
  const { t } = useTranslation('common');
  const { request } = useContext(LineItemsContext);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { isPending, mutateAsync } = useUpdateLineItemColumnLabels();
  const labels = request?.line_item_column_labels;
  const effectiveLabelFor = useColumnLabelResolver(labels);
  const buyerOnlyPriceRows = buyerOnlyPriceRowsFor(request);
  const isColumnEnabledOnEvent = (columnId: EventSettableColumnId): boolean => (
    isColumnEnabledForEvent(request, columnId).enabled
  );

  const {
    control,
    formState: { defaultValues, dirtyFields, errors, isDirty },
    handleSubmit,
    reset,
    trigger,
  } = useForm<ColumnLabelsFormValues>({
    defaultValues: buildDefaultValues(labels),
    mode: 'onChange',
    shouldUnregister: true,
  });

  const hasLabelValidationErrors = Object.keys(errors).length > 0;
  const platformDefaultLabelFor = (columnId: CanonicalColumnId): string => t(CLIENT_I18N_KEY[columnId]);
  const isColumnEditableInDrawer = (columnId: CanonicalColumnId): columnId is EventSettableColumnId => (
    columnId !== 'negotiationPricePerUnit' && isColumnEnabledOnEvent(columnId)
  );

  /**
   * Client mirror of bidops-app `app/validators/line_item_column_labels_validator.rb` — same
   * rules, same order, same normalisation — so the field goes red before the save is sent. The
   * server remains authoritative; its 422 still renders as the summary list below.
   * Only a proposed name (value differs from what the drawer opened with) is judged, as the server
   * judges touched ids only — an untouched prefill colliding with another column's default must
   * not grey out Save. Judged by value, not dirty bookkeeping, so `trigger()` sees the keystroke.
   */
  const validateProposedLabelFor = (columnId: EventSettableColumnId) => (
    proposedLabel: string,
    formValuesByColumnId: ColumnLabelsFormValues
  ): string | true => {
    const isLabelProposedForColumn = (otherColumnId: CanonicalColumnId): boolean => (
      isColumnEditableInDrawer(otherColumnId)
        && formValuesByColumnId[otherColumnId] !== (defaultValues?.[otherColumnId] ?? '')
    );

    if (!isLabelProposedForColumn(columnId)) return true;

    const otherColumnLabels = labelsReservedByOtherColumns(
      { columnId, effectiveLabelFor, formValuesByColumnId, isLabelProposedForColumn, platformDefaultLabelFor }
    );
    const validationFailure = validateColumnLabel(proposedLabel, otherColumnLabels);

    return validationFailure ? labelValidationErrorMessage(validationFailure, columnId, t) : true;
  };

  /**
   * A collision involves two fields, so editing one must re-judge the others: the field that
   * used to clash stops clashing, or a fresh clash appears on a field the buyer never touched.
   */
  const handleLabelChangeAndRevalidateAllFields = (fieldOnChange: (_event: ChangeEvent<HTMLInputElement>) => void) => (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    fieldOnChange(event);
    trigger();
  };

  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      setSaveErrors([]);
      reset(buildDefaultValues(labels));
    }

    wasOpen.current = open;
  }, [labels, open, reset]);

  /**
   * Saves only the columns the buyer actually edited, then closes.
   *
   * The payload is built from react-hook-form's dirty fields: the endpoint merges rather than
   * replaces, so an untouched field — including one pre-filled by an org-level rename — is never
   * sent, and never silently promoted into an event override. Dirtiness is by value, so a field
   * edited and retyped back to its prefill counts as untouched. If nothing is dirty there is
   * nothing to write: close without a request, and without a refresh nothing changed to repaint.
   *
   * A rejected save deliberately does not reset the form: the backend answers with messages
   * naming the offending columns, and clearing the fields would cost the buyer every other
   * edit they made in order to fix one.
   *
   * A column disabled for this event is excluded here even if the form holds a dirty value for
   * it — which can happen when a setting is switched off after the buyer started typing. Its
   * stored label must survive untouched, so it never reaches the payload, not even as `null`.
   */
  const onSubmit = async (values: ColumnLabelsFormValues): Promise<void> => {
    setSaveErrors([]);

    const changedValues = EVENT_SETTABLE_COLUMN_IDS.reduce<Partial<ColumnLabelsFormValues>>(
      (changed, columnId) => (
        dirtyFields[columnId] && isColumnEnabledOnEvent(columnId) ? { ...changed, [columnId]: values[columnId] } : changed
      ),
      {}
    );

    if (Object.keys(changedValues).length === 0) {
      onClose();

      return;
    }

    try {
      await mutateAsync({ eventId: request.id, values: changedValues });
      onClose();
    } catch (error) {
      const messages = extractLabelErrors(error);

      setSaveErrors(
        messages.length > 0
          ? messages
          : [t('bid_request.event_config_menu.column_labels.save_error')]
      );
    }
  };

  return (
    <Drawer
      dataTestId='column-labels-drawer'
      footer={(
        <RenameColumnsDrawerFooter
          canSave={isDirty && !hasLabelValidationErrors}
          isPending={isPending}
          onCancel={onClose}
          onSave={handleSubmit(onSubmit)}
        />
      )}
      onClose={onClose}
      open={open}
      title={t('bid_request.event_config_menu.column_labels.drawer_title')}
      width={DRAWER_WIDTH}
    >
      <div className={styles.content} data-testid='column-labels-drawer-content'>
        <p className={styles.description}>
          {t('bid_request.event_config_menu.column_labels.drawer_description')}
        </p>
        {saveErrors.length > 0 && (
          <ul className={styles.errorList} data-testid='column-labels-errors'>
            {saveErrors.map(errorMessage => (
              <li key={errorMessage}>
                <Typography.Text type='danger'>{errorMessage}</Typography.Text>
              </li>
            ))}
          </ul>
        )}
        <form className={styles.form}>
          {EVENT_SETTABLE_COLUMN_IDS.map(columnId => {
            const { enabled, disabledFieldTooltipKey } = isColumnEnabledForEvent(request, columnId);

            if (!enabled) {
              return (
                <DisabledColumnField
                  key={columnId}
                  columnId={columnId}
                  disabledFieldTooltipReason={disabledFieldTooltipKey ? t(disabledFieldTooltipKey) : ''}
                  effectiveLabel={effectiveLabelFor(columnId)}
                  platformDefaultLabel={platformDefaultLabelFor(columnId)}
                />
              );
            }

            return (
              <Fragment key={columnId}>
                <Controller
                  control={control}
                  name={columnId}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      caption={fieldState.error?.message}
                      data-testid={`column-labels-input-${columnId}`}
                      label={platformDefaultLabelFor(columnId)}
                      onChange={handleLabelChangeAndRevalidateAllFields(field.onChange)}
                      placeholder={platformDefaultLabelFor(columnId)}
                      status={fieldState.error ? 'error' : undefined}
                      tooltipTitle={t(columnAudienceHintKey(columnId))}
                    />
                  )}
                  rules={{ validate: validateProposedLabelFor(columnId) }}
                />
                {columnId === 'pricePerUnit' && (
                  <>
                    {buyerOnlyPriceRows.showNegotiationStartingPrice && (
                      <BuyerOnlyColumnField
                        id={CANONICAL_COLUMN_ID.negotiationPricePerUnit}
                        label={platformDefaultLabelFor(CANONICAL_COLUMN_ID.negotiationPricePerUnit)}
                        reason={t(`${BUYER_ONLY_FIELD_TOOLTIP_KEY_PREFIX}.negotiation_starting_price`)}
                        value={effectiveLabelFor(CANONICAL_COLUMN_ID.negotiationPricePerUnit)}
                      />
                    )}
                    {buyerOnlyPriceRows.showBaselinePricePerUnit && (
                      <BuyerOnlyColumnField
                        id='baselinePricePerUnit'
                        label={t('general.baseline_price_per_unit')}
                        reason={t(`${BUYER_ONLY_FIELD_TOOLTIP_KEY_PREFIX}.baseline_price_per_unit`)}
                        value={t('general.baseline_price_per_unit')}
                      />
                    )}
                  </>
                )}
              </Fragment>
            );
          })}
        </form>
      </div>
    </Drawer>
  );
};
